/* eslint-disable no-param-reassign */
/* eslint-disable max-len */
import {
  ArrowsDownUpIcon,
  CheckIcon,
  CrossIcon,
  PlusIcon,
  SearchIcon,
} from 'assets/icons';
import EmptyClipsBackgroundImage from 'assets/images/empty-clips-background.png';
import globalStyles from 'assets/stylesheets/global-styles.module.scss';
import { BottomSheet } from 'common/bottom-sheet';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { parseVaultsCollaborators } from 'common/collaborators-table/serializer';
import {
  VaultInviteOriginFilterIds,
  Breakpoints,
  CollaboratorStatusFiltersIds,
  NavSections,
  NotificationType,
} from 'common/enums';
import { Input } from 'common/input';
import {
  InvitationStep,
  InviteToRecordProcess,
} from 'common/invite-to-record-process/invite-to-record-process';
import { Select } from 'common/select';
import { Spinner } from 'common/spinner';
import { vaultsOrderByOptions } from 'config/constants';
import { appActions, AppContext } from 'context';
import { HelpInfoSections } from 'context/app-context/context-reducer';
import {
  collaboratorsActions,
  CollaboratorsContext,
} from 'context/collaborators-context';
import { LocalStorageApi } from 'helpers/local-storage';
import { classnames } from 'helpers/utils';
import { useMediaQuery } from 'hooks/use-media-query';
import { debounce } from 'lodash';
import { CollaboratorsQueryParams } from 'models/collaborators-query-params';
import { NotificationObject } from 'models/notificationObject';
import { VaultsController } from 'networking/controllers/vaults-controller';
import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { z } from 'zod';
import { ShareDialog } from 'common/share-dialog';
import { ShareTarget } from 'networking/controllers/shares-controller';
import { CollaboratorsContent } from './components/collaborators-content';
import { CollaboratorsFilters } from './components/collaborators-filters';
import { EmptyPageContent } from './components/empty-page-content';
import styles from './contributors.module.scss';

const CheckboxOptionSimpleSchema = z.object({
  id: z.string(),
  name: z.string(),
  isSelected: z.boolean(),
});

const CheckboxGroupSchema = z.object({
  id: z.string(),
  name: z.string(),
  options: z.array(CheckboxOptionSimpleSchema),
  checkValue: z.boolean().nullable().optional(),
});

const CheckboxGroupSchemaArray = z.array(CheckboxGroupSchema);

const Contributors: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  // Set when coming from inside a vault: that vault is listed first and open.
  const focusVaultId =
    Number(new URLSearchParams(location.search).get('vaultId')) || undefined;
  const { dispatch: generalDispatch, state: generalState } =
    useContext(AppContext);
  const { state: collaboratorsState, dispatch: collaboratorsDispatch } =
    useContext(CollaboratorsContext);
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  // The share dialog, for any of the listed vaults; bumping the key reloads
  // the open people lists after inviting.
  const [showShare, setShowShare] = useState(false);
  const [peopleRefreshKey, setPeopleRefreshKey] = useState(0);
  const [ownedInvites, setOwnedInvites] = useState(0);
  const [totalPendingInvites, setTotalPendingInvites] = useState(0);

  const [showInviteToRecordProcess, setShowInviteToRecordProcess] = useState<
    InvitationStep | undefined
  >(undefined);
  const [
    InviteToRecordProcessPreselectedVault,
    setInviteToRecordProcessPreselectedVault,
  ] = useState<SelectType | undefined>(undefined);

  const [isLoadingPage, setIsLoadingPage] = useState(true);

  const {
    queryParams,
    vaultsCollaborators,
    searchValue,
    refetchCollaborators,
    isLoading,
    checkboxFilters,
  } = collaboratorsState;

  const isTablet = useMediaQuery(`(max-width: ${Breakpoints.lg}px)`);
  const isMobile = useMediaQuery(`(max-width: ${Breakpoints.xs}px)`);

  const isMobileOrTablet = isTablet || isMobile;

  const didMount = useRef(false);

  const isAnyFilterApplied = Object.keys(queryParams).some(
    (param) =>
      !['pageSize', 'pageNum', 'sortBy', 'focusVaultId'].includes(param) &&
      !!queryParams[param as keyof CollaboratorsQueryParamsType],
  );

  const [hasInitializedFilters, setHasInitializedFilters] = useState(false);

  // The context outlives this page: drop the focus on leaving so a later visit
  // from the navbar shows the regular list.
  const latestQueryParams = useRef(queryParams);
  latestQueryParams.current = queryParams;
  useEffect(
    () => () => {
      if (!latestQueryParams.current.focusVaultId) return;
      collaboratorsDispatch({
        type: collaboratorsActions.UPDATE_QUERY_PARAMS,
        queryParams: {
          ...latestQueryParams.current,
          focusVaultId: undefined,
        },
      });
    },
    [],
  );

  useEffect(() => {
    if (focusVaultId) {
      // Saved search/filters could hide the vault, so skip them for this visit
      // (without touching the stored preferences).
      collaboratorsDispatch({
        type: collaboratorsActions.UPDATE_CHECKBOX_FILTERS,
        checkboxFilters: checkboxFilters.map((group) => ({
          ...group,
          options: group.options.map((opt) => ({ ...opt, isSelected: false })),
        })),
      });
      collaboratorsDispatch({
        type: collaboratorsActions.UPDATE_SEARCH_VALUE,
        searchValue: '',
      });
      collaboratorsDispatch({
        type: collaboratorsActions.UPDATE_QUERY_PARAMS,
        queryParams: new CollaboratorsQueryParams({ focusVaultId }),
      });
      // Consume the param so a reload or Back doesn't pin the vault again.
      history.replace(location.pathname);
      setHasInitializedFilters(true);
      return;
    }

    const cachedSearchValue = LocalStorageApi.get('searchValue');
    const cachedSortBy = LocalStorageApi.get('sortBy');
    const cachedCollaboratorsFilters = LocalStorageApi.get(
      'collaboratorsFilters',
    );
    const parsedResult = CheckboxGroupSchemaArray.safeParse(
      cachedCollaboratorsFilters,
    );
    if (parsedResult.success) {
      collaboratorsDispatch({
        type: collaboratorsActions.UPDATE_CHECKBOX_FILTERS,
        checkboxFilters: cachedCollaboratorsFilters,
      });
      const appliedFilters: Record<string, string> = {};

      cachedCollaboratorsFilters.forEach((filterGroup: any) => {
        const selectedOptions = filterGroup.options?.filter(
          (opt: { isSelected: boolean }) => opt.isSelected,
        );

        if (!selectedOptions || selectedOptions.length === 0) return;

        if (selectedOptions.length === 1) {
          appliedFilters[filterGroup.id] = selectedOptions[0].id;
        } else {
          appliedFilters[filterGroup.id] = selectedOptions
            .map((opt: { id: any }) => opt.id)
            .join(',');
        }
      });

      collaboratorsDispatch({
        type: collaboratorsActions.UPDATE_QUERY_PARAMS,
        queryParams: {
          ...queryParams,
          ...appliedFilters,
          pageNum: 1,
        },
      });
    } else {
      LocalStorageApi.remove('collaboratorsFilters');
    }
    if (cachedSortBy) {
      collaboratorsDispatch({
        type: collaboratorsActions.UPDATE_QUERY_PARAMS,
        queryParams: { ...queryParams, sortBy: cachedSortBy, pageNum: 1 },
      });
    }
    if (cachedSearchValue) {
      collaboratorsDispatch({
        type: collaboratorsActions.UPDATE_SEARCH_VALUE,
        searchValue: cachedSearchValue,
      });
    }

    setHasInitializedFilters(true);
  }, []);

  const fetchVaultsCollaborators = async () => {
    try {
      collaboratorsDispatch({
        type: collaboratorsActions.IS_LOADING,
        isLoading: true,
      });

      const { data, pagination } =
        await VaultsController.getVaultsCollaborators(queryParams);

      const newVaultsCollaborators = parseVaultsCollaborators(data);
      collaboratorsDispatch({
        type: collaboratorsActions.UPDATE_VAULTS,
        vaultsCollaborators: newVaultsCollaborators,
      });
      collaboratorsDispatch({
        type: collaboratorsActions.UPDATE_PAGINATION,
        pagination,
      });
    } catch (err: any) {
      generalDispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Collaborators Information',
          message: 'Error getting collaborators',
          type: NotificationType.Error,
        }),
      });
    } finally {
      collaboratorsDispatch({
        type: collaboratorsActions.IS_LOADING,
        isLoading: false,
      });
      if (!didMount.current) {
        didMount.current = true;
        setIsLoadingPage(false);
      }
      collaboratorsDispatch({
        type: collaboratorsActions.IS_SEARCHING,
        isSearching: false,
      });
    }
  };

  const getNewQueryParams = (checkBoxGroupEdited: CheckboxGroupType) => {
    const queryParamsUpdatedCopy: CollaboratorsQueryParamsType = {
      ...queryParams,
    };

    if (checkBoxGroupEdited.options.length === 2) {
      if (
        checkBoxGroupEdited.options.every((opt) => opt.isSelected) ||
        checkBoxGroupEdited.options.every((opt) => !opt.isSelected)
      ) {
        queryParamsUpdatedCopy[
          checkBoxGroupEdited.id as keyof CollaboratorsQueryParamsType
        ] = undefined;
        return queryParamsUpdatedCopy;
      }

      checkBoxGroupEdited.options.forEach((opt: CheckboxOptionSimpleType) => {
        if (opt.isSelected) {
          queryParamsUpdatedCopy[
            checkBoxGroupEdited.id as keyof Omit<
              CollaboratorsQueryParamsType,
              'pageNum' | 'pageSize' | 'sortBy' | 'focusVaultId'
            >
          ] = opt.id;
        }
      });
      return queryParamsUpdatedCopy;
    }

    // Case for filters that are values separated by commas
    let paramValues =
      queryParamsUpdatedCopy[
        checkBoxGroupEdited.id as keyof Omit<
          CollaboratorsQueryParamsType,
          'pageNum' | 'pageSize' | 'sortBy' | 'focusVaultId'
        >
      ]?.split(',') || [];
    checkBoxGroupEdited.options.forEach((opt: CheckboxOptionSimpleType) => {
      if (opt.isSelected && !paramValues.includes(opt.id as string)) {
        paramValues = [...paramValues, opt.id];
      } else if (!opt.isSelected) {
        paramValues = paramValues.filter((item) => item !== opt.id);
      }
    });
    queryParamsUpdatedCopy[
      checkBoxGroupEdited.id as keyof Omit<
        CollaboratorsQueryParamsType,
        'pageNum' | 'pageSize' | 'sortBy' | 'focusVaultId'
      >
    ] = paramValues.filter((value) => value !== '').join(',');

    return queryParamsUpdatedCopy;
  };

  const seeUserPendingInvites = async () => {
    const newFilters = checkboxFilters.map((filter) => {
      if (filter.id === 'inviteOrigin') {
        return {
          ...filter,
          options: filter.options.map((option) => {
            if (option.id === VaultInviteOriginFilterIds.myself) {
              return { ...option, isSelected: true };
            }
            return { ...option, isSelected: false };
          }),
        };
      }
      if (filter.id === 'collaboratorStatus') {
        return {
          ...filter,
          options: filter.options.map((option) => {
            if (option.id === CollaboratorStatusFiltersIds.pending) {
              return { ...option, isSelected: true };
            }
            return { ...option, isSelected: false };
          }),
        };
      }
      return {
        ...filter,
        options: filter.options.map((option) => ({
          ...option,
          isSelected: false,
        })),
      };
    });

    collaboratorsDispatch({
      type: collaboratorsActions.UPDATE_CHECKBOX_FILTERS,
      checkboxFilters: newFilters,
    });

    const newInviteOriginFilter = newFilters.find(
      (filter) => filter.id === 'inviteOrigin',
    ) as CheckboxGroupType;
    const newCollaboratorStatusFilter = newFilters.find(
      (filter) => filter.id === 'collaboratorStatus',
    ) as CheckboxGroupType;

    const inviteOriginQueryParam = getNewQueryParams(newInviteOriginFilter);

    const collaboratorStatusQueryParam = getNewQueryParams(
      newCollaboratorStatusFilter,
    );

    collaboratorsDispatch({
      type: collaboratorsActions.UPDATE_QUERY_PARAMS,
      queryParams: {
        ...queryParams,
        inviteOrigin: inviteOriginQueryParam.inviteOrigin,
        collaboratorStatus: collaboratorStatusQueryParam.collaboratorStatus,
        focusVaultId: undefined,
        pageNum: 1,
      },
    });
  };

  const fetchOwnedInvites = async () => {
    try {
      const { totalInvites, totalPendingInvitesAllVaults } =
        await VaultsController.getOwnedInvites();
      setOwnedInvites(totalInvites);
      setTotalPendingInvites(totalPendingInvitesAllVaults);
    } catch (err: any) {
      generalDispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Collaborators Information',
          message: 'Error getting owned invites',
          type: NotificationType.Error,
        }),
      });
    }
  };

  const resendPendingInvites = async (
    inviterQueryParam: ResendPendingInvitesQueryParams,
  ) => {
    try {
      const totalInvitesResent =
        await VaultsController.resendPendingInvites(inviterQueryParam);
      generalDispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: `${totalInvitesResent} invites resent.`,
          type: NotificationType.Info,
          noMessage: true,
        }),
      });
    } catch (err: any) {
      generalDispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Collaborators Information',
          message: 'Error resending pending invites',
          type: NotificationType.Error,
        }),
      });
    }
  };

  const exportAllCollaborators = async (
    contributors?: string,
    guests?: string,
  ) => {
    try {
      await VaultsController.exportVaultCollaborators({
        ...queryParams,
        format: 'csv',
        contributors,
        guests,
      });
    } catch (err: any) {
      generalDispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Collaborators Information',
          message: 'Error exporting collaborators',
          type: NotificationType.Error,
        }),
      });
    }
  };

  useEffect(() => {
    generalDispatch({
      type: appActions.ACTUAL_SECTION,
      actualSection: NavSections.collaborators,
    });
    generalDispatch({
      type: appActions.SET_HELP_SECTION,
      helpSection: HelpInfoSections.contributors,
    });

    if (hasInitializedFilters) {
      fetchVaultsCollaborators();
    }

    return () => {
      generalDispatch({
        type: appActions.SET_HELP_SECTION,
      });
    };
  }, [hasInitializedFilters]);

  useEffect(() => {
    if (!hasInitializedFilters) return;
    collaboratorsDispatch({
      type: collaboratorsActions.IS_SEARCHING,
      isSearching: true,
    });
    fetchVaultsCollaborators();
  }, [queryParams, hasInitializedFilters]);

  useEffect(() => {
    if (refetchCollaborators) {
      fetchVaultsCollaborators();
      collaboratorsDispatch({
        type: collaboratorsActions.SET_REFETCH_COLLABORATORS,
        refetchCollaborators: false,
      });
      fetchOwnedInvites();
    }
  }, [refetchCollaborators]);

  useEffect(() => {
    fetchOwnedInvites();
  }, []);

  const handleDebounceFn = (newValue: string) => {
    const params = { ...queryParams };
    if (newValue === '') delete params.search;
    else params.search = newValue;

    collaboratorsDispatch({
      type: collaboratorsActions.UPDATE_QUERY_PARAMS,
      queryParams: { ...params, focusVaultId: undefined, pageNum: 1 },
    });
  };

  const debounceFn = useCallback(debounce(handleDebounceFn, 1000), [
    queryParams,
  ]);

  const handleChangeSearch = (newValue: string) => {
    LocalStorageApi.set('searchValue', newValue);
    collaboratorsDispatch({
      type: collaboratorsActions.IS_SEARCHING,
      isSearching: false,
    });
    collaboratorsDispatch({
      type: collaboratorsActions.UPDATE_SEARCH_VALUE,
      searchValue: newValue,
    });

    debounceFn(newValue);
  };

  const handleClickSortBy = (option: SelectType) => {
    LocalStorageApi.set('sortBy', option);
    collaboratorsDispatch({
      type: collaboratorsActions.UPDATE_QUERY_PARAMS,
      queryParams: {
        ...queryParams,
        sortBy: option,
        focusVaultId: undefined,
        pageNum: 1,
      },
    });
  };

  const handleAllNewFiltersSelected = (
    checkBoxGroupEdited: CheckboxGroupType,
  ) => {
    const newQueryParams = getNewQueryParams(checkBoxGroupEdited);
    collaboratorsDispatch({
      type: collaboratorsActions.UPDATE_QUERY_PARAMS,
      queryParams: { ...newQueryParams, focusVaultId: undefined, pageNum: 1 },
    });
  };

  const showContributorsTable = () => (
    <CollaboratorsContent
      vaults={vaultsCollaborators}
      user={generalState.data.user}
      notifyShowInviteToRecordProcess={(step: InvitationStep) =>
        setShowInviteToRecordProcess(step)
      }
      peopleRefreshKey={peopleRefreshKey}
      notifyExportAll={exportAllCollaborators}
      notifyResendPendingInvites={resendPendingInvites}
      notifySeeMyPendingInvites={seeUserPendingInvites}
      ownedInvites={ownedInvites}
      totalPendingInvites={totalPendingInvites}
    />
  );

  const renderSearchContent = () => (
    <>
      <Input
        className={styles.inputSearch}
        id="search"
        name="search"
        placeholder={isMobile ? 'Search' : 'Search by name or vault'}
        value={searchValue}
        withValidation={false}
        withIconStart={<SearchIcon className={classnames(styles.searchIcon)} />}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          handleChangeSearch(e.target.value)
        }
        innerClassName={styles.inputSearchInner}
        withIconEnd={
          searchValue && (
            <CrossIcon
              className={styles.clearSearchIcon}
              onClick={() => handleChangeSearch('')}
            />
          )
        }
      />
      <div className={styles.sortVaultsBy}>
        <span className="text__body__semi__bold__medium__gray700">
          Sort vaults by
        </span>
        <Select
          options={vaultsOrderByOptions}
          optionSelected={queryParams.sortBy!}
          onClickOption={(option) => handleClickSortBy(option)}
          id="sortByOption"
          value={queryParams.sortBy!.value}
          placeholder="Select option"
        />
      </div>
      <Button
        className={styles.sortButton}
        buttonStyle={ButtonStyle.PrimaryStroke}
        buttonSize={ButtonSize.Small}
        onClick={() => setShowBottomSheet(true)}
      >
        <ArrowsDownUpIcon />
      </Button>
      <CollaboratorsFilters
        notifyAllNewFiltersSelected={handleAllNewFiltersSelected}
      />
    </>
  );

  const showTitleAndFilters = () => (
    <div className={styles.titleAndFilters}>
      <div className={styles.title}>
        <span className="text__heading4__gray800">Collaborators</span>
        <Button
          className={classnames(styles.inviteButton, {
            [styles.notDisplay]: !isMobileOrTablet,
          })}
          buttonSize={ButtonSize.Small}
          onClick={() => setShowShare(true)}
        >
          <PlusIcon />
          {!isMobile && 'Invite'}
        </Button>
      </div>
      {isMobileOrTablet ? (
        <div className={styles.searchContainerMobile}>
          {renderSearchContent()}
        </div>
      ) : (
        renderSearchContent()
      )}
    </div>
  );

  const showEmptyPage = () => (
    <div className={styles.emptyPage}>
      <div className={styles.title}>
        <span className="text__heading4__gray800">Collaborators</span>
        {vaultsCollaborators.length > 0 && (
          <Button
            className={classnames(styles.inviteButton)}
            buttonSize={ButtonSize.Small}
            onClick={() => setShowShare(true)}
          >
            <PlusIcon />
            {!isMobile && 'Invite'}
          </Button>
        )}
      </div>
      <img alt="background-image" src={EmptyClipsBackgroundImage} />
      <div className={styles.emptyMessageContainer}>
        <div className={styles.emptyMessageContent}>
          <EmptyPageContent
            vaultsCollaborators={vaultsCollaborators}
            notifyInviteToRecordProcess={() => setShowShare(true)}
          />
        </div>
      </div>
    </div>
  );

  const renderEmptyPge =
    vaultsCollaborators.length === 0 && !isAnyFilterApplied && !isLoading;

  const showContentView = () => {
    if (renderEmptyPge) {
      return showEmptyPage();
    }
    return (
      <>
        {showTitleAndFilters()}
        {showContributorsTable()}
      </>
    );
  };

  // Every listed vault; the one in focus (or the first) comes preselected.
  const shareChoices: ShareTarget[] = vaultsCollaborators.map((vault) => ({
    type: 'vault',
    vaultId: vault.id,
    id: vault.id,
    name: vault.title,
  }));
  const shareStart =
    shareChoices.find((choice) => choice.vaultId === focusVaultId) ||
    shareChoices[0];

  const handleInviteClose = () => {
    setShowInviteToRecordProcess(undefined);
    setInviteToRecordProcessPreselectedVault(undefined);
  };
  return (
    <>
      {showShare && shareStart && (
        <ShareDialog
          target={shareStart}
          choices={shareChoices}
          onClose={() => {
            setShowShare(false);
            setPeopleRefreshKey((key) => key + 1);
          }}
        />
      )}
      {!!showInviteToRecordProcess && (
        <InviteToRecordProcess
          handleClose={handleInviteClose}
          initialStep={showInviteToRecordProcess}
          handleFinishProcess={() => {
            collaboratorsDispatch({
              type: collaboratorsActions.SET_REFETCH_COLLABORATORS,
              refetchCollaborators: true,
            });
          }}
          withVaultSelection
          preselectedVault={InviteToRecordProcessPreselectedVault}
        />
      )}
      <div
        className={classnames(
          globalStyles.loggedMainContainer,
          styles.paddingBottomDiv,
        )}
      >
        <div className={styles.contributorsContent}>
          {!isLoadingPage ? showContentView() : <Spinner />}
        </div>
        <BottomSheet
          className={styles.bottomSheet}
          open={showBottomSheet}
          onDismiss={() => setShowBottomSheet(false)}
        >
          <div className={styles.bottomSheetContent}>
            <div className={styles.bottomSheetHeader}>
              <span className="text__body__semi__bold__medium__gray800">
                Sort vaults by
              </span>
              <button type="button" onClick={() => setShowBottomSheet(false)}>
                <CrossIcon />
              </button>
            </div>
            {vaultsOrderByOptions.map((option) => (
              <button
                type="button"
                className={styles.bottomSheetOrderOption}
                key={option.id}
                onClick={() => handleClickSortBy(option)}
              >
                <span
                  className={
                    queryParams.sortBy?.id === option.id
                      ? 'text__body__bold__small__brand500'
                      : 'text__body__regular__small__gray800'
                  }
                >
                  {option.value}
                </span>
                {queryParams.sortBy?.id === option.id && <CheckIcon />}
              </button>
            ))}
          </div>
        </BottomSheet>
      </div>
    </>
  );
};

export { Contributors };
